import { onBeforeUnmount, shallowRef, watch, type Ref } from 'vue'
import { getPageData, openDataStream, type DataQuery, type DataStream, type ViewData } from '@/lib/api'
import { RANGE_UNIT_MS, type RangeState } from '@/stores/range'
import type { VariableValues } from '@/types'

const UNIT_SUFFIX = 'm'

export const getRangeQuery = (state: RangeState, variables: VariableValues): DataQuery => {
  const query: DataQuery = { variables }
  if (state.mode === 'relative') {
    const minutes = Math.round((state.count * RANGE_UNIT_MS[state.unit]) / 60_000)
    query.from = `${minutes}${UNIT_SUFFIX}`
  }
  if (state.mode === 'absolute' && state.from && state.to) {
    query.from = new Date(state.from).toISOString()
    query.to = new Date(state.to).toISOString()
  }
  return query
}

const placementData = new Map<string, ViewData>()

export const usePageData = (pageId: Ref<string | null>, query: Ref<DataQuery>, live: Ref<boolean>) => {
  const views = shallowRef<Record<string, ViewData>>({})
  const error = shallowRef<string | null>(null)
  let stream: DataStream | null = null
  let pending: Record<string, ViewData> = {}
  let frame: number | null = null

  const flush = () => {
    frame = null
    for (const [id, data] of Object.entries(pending)) placementData.set(id, data)
    views.value = { ...views.value, ...pending }
    pending = {}
  }

  const putData = (id: string, data: ViewData) => {
    pending[id] = data
    if (frame == null) frame = window.requestAnimationFrame(flush)
  }

  const close = () => {
    stream?.close()
    stream = null
  }

  const getData = async (id: string) => {
    try {
      const data = await getPageData(id, query.value)
      error.value = null
      for (const [placement, value] of Object.entries(data.views)) putData(placement, value)
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Page data failed'
    }
  }

  const open = () => {
    close()
    const id = pageId.value
    if (!id) return
    if (!live.value) {
      void getData(id)
      return
    }
    stream = openDataStream(`/pages/${id}/data`, query.value, (f) => {
      if (f.type === 'error') {
        error.value = f.error ?? 'Page data failed'
        return
      }
      if (!f.id || !f.data) return
      error.value = null
      putData(f.id, f.data)
    })
  }

  watch([pageId, live], () => {
    views.value = Object.fromEntries([...placementData])
    open()
  }, { immediate: true })

  watch(query, open, { deep: true })

  onBeforeUnmount(() => {
    close()
    if (frame != null) window.cancelAnimationFrame(frame)
  })

  return { views, error }
}
